<template>
	<div class="container flip-clock" 
		 role="timer" 
		 aria-live="polite"
		 :aria-label="timerAriaLabel">
		
		<!-- Screen reader friendly time display -->
		<div class="sr-only" aria-live="polite">
			{{ screenReaderTimeText }}
		</div>
		
		<template v-for="(data, index) in timeData"
				  v-show="show">
            <span :id="data.elementId"
				  class="flip-clock__piece"
				  v-bind:key="data.label"
				  v-if="index > 0"
				  role="img"
				  :aria-label="`${data.current} ${data.label.toLowerCase()}`">
                <span class="flip-clock__card flip-card">
                <b class="flip-card__top" aria-hidden="true">{{data.current | twoDigits}}</b>
                <b class="flip-card__bottom"
				   v-bind:data-value="data.current | twoDigits"
				   aria-hidden="true"></b>
                <b class="flip-card__back"
				   v-bind:data-value="data.previous | twoDigits"
				   aria-hidden="true"></b>
                <b class="flip-card__back-bottom"
				   v-bind:data-value="data.previous | twoDigits"
				   aria-hidden="true"></b>
                </span>
                <span class="flip-clock__slot sr-only" aria-hidden="true">{{data.label}}</span>
            </span>
		</template>
	</div>
</template>

<script>

	import LightenDarkenColor from "./../utils/color";

    let interval = null;
    const uuidv4 = require('uuid/v4');



    export default {
        name: 'flipCountdown',
        props: {
            stop: {
                type: Boolean
            },
            fontFamily: {
              type: String
            },
            labels: {
                type: Object,
                required: false,
                default: function () {
                    return {
                        days: 'Days',
                        hours: 'Hours',
                        minutes: 'Minutes',
                        seconds: 'Seconds'
                    }
                }
            }
        },
        data() {
            const uuid = uuidv4()
            return {
                now: Math.trunc(new Date().getTime() / 1000),
                date: null,
                interval: null,
                diff: 0,
                show: false,
                timeData: [
                    {
                        current: 0,
                        previous: 0,
                        label: this.$t('Days'),
                        elementId: 'flip-card-days-' + uuid
                    },
                    {
                        current: 0,
                        previous: 0,
                        label: this.$t('Hours'),
                        elementId: 'flip-card-hours-' + uuid
                    },
                    {
                        current: 0,
                        previous: 0,
                        label: this.$t('Minutes'),
                        elementId: 'flip-card-minutes-' + uuid
                    },
                    {
                        current: 0,
                        previous: 0,
                        label: this.$t('Seconds'),
                        elementId: 'flip-card-seconds-' + uuid
                    }
                ]
            }
        },
        created() {


        },
        mounted() {


            if (this.diff !== 0) {
                this.show = true
            }
        },
        computed: {
            seconds() {
                return Math.trunc(this.diff) % 60
            },
            minutes() {
                return Math.trunc(this.diff / 60) % 60
            },
            hours() {
                return Math.trunc(this.diff / 60 / 60) % 24
            },
            days() {
                return Math.trunc(this.diff / 60 / 60 / 24)
            },
			timerAriaLabel() {
				if (this.diff <= 0) {
					return this.$t('Accessibility.TimerExpired');
				}
				return `${this.$t('Accessibility.GameTimer')}: ${this.screenReaderTimeText}`;
			},
			screenReaderTimeText() {
				if (this.diff <= 0) {
					return this.$t('Accessibility.TimerExpired');
				}
				
				let parts = [];
				if (this.days > 0) {
					parts.push(`${this.days} ${this.days === 1 ? this.$t('Day') : this.$t('Days')}`);
				}
				if (this.hours > 0) {
					parts.push(`${this.hours} ${this.hours === 1 ? this.$t('Hour') : this.$t('Hours')}`);
				}
				if (this.minutes > 0) {
					parts.push(`${this.minutes} ${this.minutes === 1 ? this.$t('Minute') : this.$t('Minutes')}`);
				}
				if (this.seconds > 0 || parts.length === 0) {
					parts.push(`${this.seconds} ${this.seconds === 1 ? this.$t('Second') : this.$t('Seconds')}`);
				}
				
				return parts.join(', ') + ' ' + this.$t('Accessibility.TimeRemaining');
			}
        },
        watch: {
            now(value) {

                this.diff = this.date - this.now

                if (this.stop) {
                    clearInterval(this.interval);
                    this.$emit('stopped', Math.round(this.diff));
                    return;
                }


                if (this.diff <= 0) {
                    this.diff = 0
                    this.updateTime(3, 0)
                    clearInterval(this.interval)
                } else {
                    this.updateTime(0, this.days)
                    this.updateTime(1, this.hours)
                    this.updateTime(2, this.minutes)
                    this.updateTime(3, this.seconds)
                }
                if (this.diff <= 0) {
                    this.$emit('end');

                }
            }
        },
        filters: {
            twoDigits(value) {
                if (value.toString().length <= 1) {
                    return '0' + value.toString()
                }
                return value.toString()
            }
        },
        methods: {
            start(seconds, textColor, backgroundColor, topBackgroundColor, fontFamily, timerTopTextColor) {

                if (!textColor || !backgroundColor) {
                    textColor = '#ffdc00';
                    backgroundColor = '#393939';
                }

                this.timeData[0].label = this.$t('Days');
                this.timeData[1].label = this.$t('Hours');
                this.timeData[2].label = this.$t('Minutes');
                this.timeData[3].label = this.$t('Seconds');


                let textColorTop = LightenDarkenColor(textColor, -67);
                let backgroundColorTop = LightenDarkenColor(backgroundColor, -25);
                if(topBackgroundColor && topBackgroundColor !== '') {
                    backgroundColorTop = topBackgroundColor;
                }

              if(timerTopTextColor && timerTopTextColor !== '') {
                textColorTop = timerTopTextColor;
              }

                this.$el.style.setProperty('--text-color', textColor);
                this.$el.style.setProperty('--background-color', backgroundColor);

                this.$el.style.setProperty('--text-color-top', textColorTop);
                this.$el.style.setProperty('--background-color-top', backgroundColorTop);

                if(fontFamily)
                  this.$el.style.setProperty('--font-family', fontFamily);



                const dt = new Date();
                dt.setSeconds(dt.getSeconds() + seconds);
                this.date = dt.getTime() / 1000;

                this.now = Math.trunc(new Date().getTime() / 1000)

                this.interval = setInterval(() => {
                    this.now = Math.trunc(new Date().getTime() / 1000)
                }, 1000)

            },

            updateTime(idx, newValue) {
                if (idx >= this.timeData.length || newValue === undefined) {
                    return
                }

                if (window['requestAnimationFrame']) {
                    this.frame = requestAnimationFrame(this.updateTime.bind(this))
                }

                const d = this.timeData[idx]
                const val = (newValue < 0 ? 0 : newValue)

                if (val !== d.current) {
                    d.previous = d.current
                    d.current = val

                    const el = document.querySelector(`#${d.elementId}`)
                    if (el) {
                        el.classList.remove('flip')
                        void el.offsetWidth
                        el.classList.add('flip')
                    }
                }
            },


        },
        beforeDestroy() {
            if (window['cancelAnimationFrame']) {
                cancelAnimationFrame(this.frame)
            }
        },
        destroyed() {
            clearInterval(interval)
        }
    }
</script>

<style lang="less"
	   scoped>

	/* Screen reader only content */
	.sr-only {
		position: absolute !important;
		width: 1px !important;
		height: 1px !important;
		padding: 0 !important;
		margin: -1px !important;
		overflow: hidden !important;
		clip: rect(0, 0, 0, 0) !important;
		white-space: nowrap !important;
		border: 0 !important;
	}

	:root {
		--text-color: #ffdc00;
		--background-color: #393939;
		--text-color-top: #cca900;
		--background-color-top: #222222;
	}

	.container {

		font-family: 'Avenir', Helvetica, Arial, sans-serif;
		-webkit-font-smoothing: antialiased;
		-moz-osx-font-smoothing: grayscale;

	}

	.flip-clock {
		text-align: center;
		perspective: 600px;
		margin: 0 auto;

		*,
		*:before,
		*:after {
			box-sizing: border-box;
		}
	}


	.flip-clock__piece {
		display: inline-block;
		margin: 0 0.2vw;

		@media (min-width: 1000px) {
			margin: 0 5px;
		}
	}

	.flip-clock__slot {
		font-size: 0.8rem;
		line-height: 1.5;
		display: block;
    font-family: var(--font-family);
	}

	@halfHeight: 0.72em;
	@borderRadius: 0.15em;

	.flip-card {
		display: block;
		position: relative;
		padding-bottom: @halfHeight;
		font-size: 2.25rem;
		line-height: 0.95;
    font-family: var(--font-family);
	}

	@media (min-width: 1000px) {
		.flip-clock__slot {
			font-size: 1.2rem;
		}

		.flip-card {
			font-size: 3rem;
		}
	}

	.flip-card__top,
	.flip-card__bottom,
	.flip-card__back-bottom,
	.flip-card__back::before,
	.flip-card__back::after {
		display: block;
		height: @halfHeight;
		color: var(--text-color-top);
		background: var(--background-color-top);
		padding: 0.23em 0.15em 0.4em;
		border-radius: @borderRadius @borderRadius 0 0;
		backface-visibility: hidden;
		-webkit-backface-visibility: hidden;
		transform-style: preserve-3d;
		width: 2.1em;
		height: @halfHeight;
	}

	.flip-card__bottom,
	.flip-card__back-bottom {
		color: var(--text-color);
		position: absolute;
		top: 50%;
		left: 0;
		border-top: solid 1px #000;
		background: var(--background-color);
		border-radius: 0 0 @borderRadius @borderRadius;
		pointer-events: none;
		overflow: hidden;
		z-index: 2;
	}

	.flip-card__back-bottom {
		z-index: 1;
	}

	.flip-card__bottom::after,
	.flip-card__back-bottom::after {
		display: block;
		margin-top: -@halfHeight;
	}

	.flip-card__back::before,
	.flip-card__bottom::after,
	.flip-card__back-bottom::after {
		content: attr(data-value);
	}

	.flip-card__back {
		position: absolute;
		top: 0;
		height: 100%;
		left: 0%;
		pointer-events: none;
	}

	.flip-card__back::before {
		position: relative;
		overflow: hidden;
		z-index: -1;
	}

	.flip .flip-card__back::before {
		z-index: 1;
		animation: flipTop 0.3s cubic-bezier(.37, .01, .94, .35);
		animation-fill-mode: both;
		transform-origin: center bottom;
	}

	.flip .flip-card__bottom {
		transform-origin: center top;
		animation-fill-mode: both;
		animation: flipBottom 0.6s cubic-bezier(.15, .45, .28, 1);
	}

	@keyframes flipTop {
		0% {
			transform: rotateX(0deg);
			z-index: 2;
		}
		0%, 99% {
			opacity: 1;
		}
		100% {
			transform: rotateX(-90deg);
			opacity: 0;
		}
	}

	@keyframes flipBottom {
		0%, 50% {
			z-index: -1;
			transform: rotateX(90deg);
			opacity: 0;
		}
		51% {
			opacity: 1;
		}
		100% {
			opacity: 1;
			transform: rotateX(0deg);
			z-index: 5;
		}
	}
</style>
